import { API_BASE_URL } from '../config/api';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: any;
  headers?: Record<string, string>;
  requireAuth?: boolean;
}

export class ApiService {
  // Callback để xử lý logout sẽ được set từ bên ngoài
  private static unauthorizedCallback: (() => void) | null = null;

  // Hàm để set callback từ bên ngoài
  public static setUnauthorizedCallback(callback: () => void): void {
    this.unauthorizedCallback = callback;
  }

  private static getHeaders(requireAuth = true): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (requireAuth) {
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  public static async request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const { 
      method = 'GET', 
      body, 
      headers = {}, 
      requireAuth = true 
    } = options;

    const url = `${API_BASE_URL}${endpoint}`;
    
    const requestOptions: RequestInit = {
      method,
      headers: {
        ...this.getHeaders(requireAuth),
        ...headers
      }
    };

    if (body) {
      requestOptions.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(url, requestOptions);
      
      // Kiểm tra status code 401 Unauthorized
      if (response.status === 401) {
        console.error('401 Unauthorized response received');
        
        // Xóa token và user từ localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Gọi callback để logout nếu có
        if (this.unauthorizedCallback) {
          this.unauthorizedCallback();
        } else {
          const currentPath = window.location.pathname;
          if (currentPath !== '/login' && currentPath !== '/register') {
            window.location.href = '/login';
          }
        }
        
        throw new Error('Unauthorized: Please login again');
      }
      
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Something went wrong');
      }

      return data;
    } catch (error) {
      console.error(`API Error (${method} ${endpoint}):`, error);
      throw error;
    }
  }

  public static get<T>(endpoint: string, requireAuth = true): Promise<T> {
    return this.request<T>(endpoint, { requireAuth });
  }

  public static post<T>(endpoint: string, body: any, requireAuth = true): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body, requireAuth });
  }

  public static put<T>(endpoint: string, body: any, requireAuth = true): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', body, requireAuth });
  }

  public static patch<T>(endpoint: string, body: any, requireAuth = true): Promise<T> {
    return this.request<T>(endpoint, { method: 'PATCH', body, requireAuth });
  }

  public static delete<T>(endpoint: string, requireAuth = true): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', requireAuth });
  }
} 